// @ts-nocheck
import { Card, Code, Divider, Group, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import {
  GradientSpinner,
  ProgressBar,
  PulseLoader,
  Shimmer,
  SkeletonGroup,
  SpinnerDots,
} from '@/components/animations/loading';

const SKELETON_PRESETS = [
  'card',
  'list-item',
  'avatar-text',
  'paragraph',
  'image-card',
  'table-row',
  'form-field',
  'stat-card',
];

const SPINNER_DOTS_VARIANTS = ['bounce', 'fade', 'scale', 'wave'];

const GRADIENT_VARIANTS = ['primary', 'secondary', 'tertiary'];

export default function LoadingPage() {
  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Loading Animations</Title>
        <Text c="dimmed" size="sm">
          Spinners, progress bars, shimmer effects, and skeleton placeholders for
          communicating loading and busy states across the app
        </Text>
      </Stack>

      {/* SpinnerDots */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>SpinnerDots</Title>
            <Text size="sm" c="dimmed">
              A row of animated dots with four motion styles. Great for inline
              "loading" indicators inside buttons or small panels.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Variants:</Text>
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
              {SPINNER_DOTS_VARIANTS.map((variant) => (
                <Card key={variant} variant="subtle" p="md" ta="center">
                  <Stack gap="sm" align="center">
                    <SpinnerDots variant={variant} size={12} />
                    <Text size="xs" c="dimmed">{variant}</Text>
                  </Stack>
                </Card>
              ))}
            </SimpleGrid>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<SpinnerDots variant="bounce" size={12} />
<SpinnerDots variant="fade" size={12} />
<SpinnerDots variant="scale" size={12} />
<SpinnerDots variant="wave" size={12} />`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • variant - 'bounce' | 'fade' | 'scale' | 'wave' (default: 'bounce')
              <br />
              • size - Dot size in pixels (default: 10)
              <br />
              • count - Number of dots (default: 3)
              <br />
              • speed - Animation cycle in ms (default: 1400)
              <br />
              • gradientVariant - 'primary' | 'secondary' | 'tertiary' (default: 'primary')
              <br />
              • color - Override dot color
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* GradientSpinner */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>GradientSpinner</Title>
            <Text size="sm" c="dimmed">
              A circular spinner with a conic gradient sweep, themed to the active
              gradient palette. Ideal for full-section loading overlays.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Sizes &amp; Gradients:</Text>
            <Group align="center" gap="xl">
              <Stack gap="xs" align="center">
                <GradientSpinner size={32} thickness={3} />
                <Text size="xs" c="dimmed">Small</Text>
              </Stack>
              <Stack gap="xs" align="center">
                <GradientSpinner size={48} thickness={4} />
                <Text size="xs" c="dimmed">Medium</Text>
              </Stack>
              <Stack gap="xs" align="center">
                <GradientSpinner size={64} thickness={6} gradientVariant="secondary" />
                <Text size="xs" c="dimmed">Large / secondary</Text>
              </Stack>
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<GradientSpinner size={48} thickness={4} />
<GradientSpinner size={64} thickness={6} gradientVariant="secondary" />`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • size - Diameter in pixels (default: 40)
              <br />
              • thickness - Ring thickness in pixels (default: 4)
              <br />
              • speed - Rotation duration in ms (default: 1000)
              <br />
              • gradientVariant - 'primary' | 'secondary' | 'tertiary' (default: 'primary')
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* PulseLoader */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>PulseLoader</Title>
            <Text size="sm" c="dimmed">
              Expanding concentric rings that pulse outward. Subtle and
              attention-light, useful for background or ambient loading.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Ring Counts &amp; Gradients:</Text>
            <Group align="center" gap="xl" mih={80}>
              <Stack gap="xs" align="center">
                <PulseLoader size={48} rings={2} />
                <Text size="xs" c="dimmed">2 rings</Text>
              </Stack>
              <Stack gap="xs" align="center">
                <PulseLoader size={48} rings={3} gradientVariant="secondary" />
                <Text size="xs" c="dimmed">3 rings / secondary</Text>
              </Stack>
              <Stack gap="xs" align="center">
                <PulseLoader size={56} rings={3} speed={2000} gradientVariant="tertiary" />
                <Text size="xs" c="dimmed">Slow / tertiary</Text>
              </Stack>
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<PulseLoader size={48} rings={2} />
<PulseLoader size={48} rings={3} gradientVariant="secondary" />
<PulseLoader size={56} rings={3} speed={2000} gradientVariant="tertiary" />`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • size - Diameter in pixels (default: 40)
              <br />
              • rings - Number of pulsing rings (default: 2)
              <br />
              • speed - Pulse duration in ms (default: 1500)
              <br />
              • gradientVariant - 'primary' | 'secondary' | 'tertiary' (default: 'primary')
              <br />
              • color - Override ring color
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* ProgressBar */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>ProgressBar</Title>
            <Text size="sm" c="dimmed">
              A slim gradient progress bar supporting determinate values, an
              indeterminate sweep, and an animated striped style.
            </Text>
          </Stack>

          <Stack gap="md">
            <Text size="sm" fw={500}>Examples:</Text>

            <Stack gap={6}>
              <Text size="xs" c="dimmed">Determinate (35%)</Text>
              <ProgressBar value={35} height={8} />
            </Stack>

            <Stack gap={6}>
              <Text size="xs" c="dimmed">Determinate (80%, secondary)</Text>
              <ProgressBar value={80} height={8} gradientVariant="secondary" />
            </Stack>

            <Stack gap={6}>
              <Text size="xs" c="dimmed">Striped &amp; animated (60%)</Text>
              <ProgressBar value={60} height={12} striped animated />
            </Stack>

            <Stack gap={6}>
              <Text size="xs" c="dimmed">Indeterminate (no value)</Text>
              <ProgressBar height={6} />
            </Stack>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<ProgressBar value={35} height={8} />
<ProgressBar value={80} height={8} gradientVariant="secondary" />
<ProgressBar value={60} height={12} striped animated />
{/* Omit value for an indeterminate sweep */}
<ProgressBar height={6} />`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • value - 0-100; omit for an indeterminate bar
              <br />
              • height - Bar height in pixels (default: 4)
              <br />
              • striped - Diagonal stripe pattern (default: false)
              <br />
              • animated - Animate stripes when striped (default: true)
              <br />
              • radius - Corner radius (default: 'xl')
              <br />
              • gradientVariant - 'primary' | 'secondary' | 'tertiary' (default: 'primary')
              <br />
              • color - Override bar fill
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Shimmer */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Shimmer</Title>
            <Text size="sm" c="dimmed">
              A shimmering gradient block for placeholder content. Use standalone as
              a sized bar, or wrap children to toggle the shimmer with the active prop.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Placeholder Lines:</Text>
            <Stack gap="sm">
              <Shimmer height={20} />
              <Shimmer height={16} width="80%" />
              <Shimmer height={16} width="60%" />
              <Group gap="sm" align="center">
                <Shimmer width={56} height={56} radius="xl" />
                <Stack gap={8} style={{ flex: 1 }}>
                  <Shimmer height={14} width="40%" />
                  <Shimmer height={12} width="70%" />
                </Stack>
              </Group>
            </Stack>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Shimmer height={20} />
<Shimmer height={16} width="80%" />

{/* Wrap children and toggle with \`active\` */}
<Shimmer active={isLoading}>
  <Text>Loaded content</Text>
</Shimmer>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • width - Width in px or string (default: '100%')
              <br />
              • height - Height in px or string (default: 20)
              <br />
              • radius - Corner radius (default: 'sm')
              <br />
              • duration - Shimmer cycle in ms (default: 1500)
              <br />
              • active - When false, renders children with no shimmer (default: true)
              <br />
              • baseColor / highlightColor - Override the gradient colors
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* SkeletonGroup */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>SkeletonGroup</Title>
            <Text size="sm" c="dimmed">
              Prebuilt skeleton layouts for common UI patterns. Pick a preset and a
              count to mirror the shape of content while it loads.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>list-item preset (count=3):</Text>
            <Card variant="subtle" p="sm">
              <SkeletonGroup preset="list-item" count={3} />
            </Card>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>stat-card preset (count=2):</Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <Card variant="subtle" p={0}>
                <SkeletonGroup preset="stat-card" count={1} />
              </Card>
              <Card variant="subtle" p={0}>
                <SkeletonGroup preset="stat-card" count={1} />
              </Card>
            </SimpleGrid>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>paragraph preset:</Text>
            <Card variant="subtle" p="md">
              <SkeletonGroup preset="paragraph" count={1} />
            </Card>
          </Stack>

          <Divider />

          <Stack gap="xs">
            <Text size="sm" fw={500}>Available presets:</Text>
            <Group gap="xs">
              {SKELETON_PRESETS.map((preset) => (
                <Code key={preset}>{preset}</Code>
              ))}
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<SkeletonGroup preset="list-item" count={3} />
<SkeletonGroup preset="stat-card" count={1} />
<SkeletonGroup preset="paragraph" />`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • preset - 'card' | 'list-item' | 'avatar-text' | 'paragraph' |
              'image-card' | 'table-row' | 'form-field' | 'stat-card' (default: 'card')
              <br />
              • count - Number of repeated skeletons (default: 1)
              <br />
              • animate - Toggle Mantine skeleton animation (default: true)
              <br />
              • gap - Spacing between repeats (default: 'md')
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Choosing a Loader</Title>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>Inline / button busy state</Text>
              <Text size="xs" c="dimmed">
                Use SpinnerDots or a small GradientSpinner inside buttons and form
                rows where space is tight.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Determinate work</Text>
              <Text size="xs" c="dimmed">
                Use ProgressBar with a value for uploads, imports, and multi-step
                jobs; drop the value for unknown-duration tasks.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Content placeholders</Text>
              <Text size="xs" c="dimmed">
                Use SkeletonGroup presets or Shimmer to preserve layout and reduce
                perceived wait while lists and cards fetch.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Ambient / full-section</Text>
              <Text size="xs" c="dimmed">
                Use GradientSpinner or PulseLoader centered in an empty section or
                overlay for page-level loading.
              </Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
